interface ErrorReportPayload {
  message: string;
  stack?: string;
  componentStack?: string;
}

function getSentryDsn(): string | undefined {
  return process.env.REACT_APP_SENTRY_DSN?.trim() || undefined;
}

function parseSentryDsn(dsn: string): { host: string; projectId: string; publicKey: string } | null {
  try {
    const url = new URL(dsn);
    const projectId = url.pathname.replace(/^\//, '');
    const publicKey = url.username;
    if (!projectId || !publicKey) return null;
    return { host: url.host, projectId, publicKey };
  } catch {
    return null;
  }
}

export function reportClientError(payload: ErrorReportPayload): void {
  if (process.env.NODE_ENV !== 'production') return;

  const dsn = getSentryDsn();
  if (!dsn) return;

  const parsed = parseSentryDsn(dsn);
  if (!parsed) return;

  const eventId = crypto.randomUUID().replace(/-/g, '');
  const envelope = {
    event_id: eventId,
    timestamp: new Date().toISOString(),
    platform: 'javascript',
    level: 'error',
    message: payload.message,
    exception: payload.stack
      ? { values: [{ type: 'Error', value: payload.message, stacktrace: { frames: [{ filename: payload.stack }] } }] }
      : undefined,
    extra: payload.componentStack ? { componentStack: payload.componentStack } : undefined,
  };

  const storeUrl = `https://${parsed.host}/api/${parsed.projectId}/store/?sentry_key=${parsed.publicKey}&sentry_version=7`;

  void fetch(storeUrl, {
    method: 'POST',
    headers: { 'Content-Type': 'application/json' },
    body: JSON.stringify(envelope),
  }).catch(() => {
    // ignore reporting failures
  });
}

let globalHandlersInstalled = false;

/**
 * Report uncaught errors and unhandled promise rejections.
 * Call once at app bootstrap; no-ops outside production or without a DSN.
 */
export function installGlobalErrorReporting(): void {
  if (globalHandlersInstalled || typeof window === 'undefined') return;
  globalHandlersInstalled = true;

  window.addEventListener('error', (event: ErrorEvent) => {
    reportClientError({
      message: event.message || 'Uncaught error',
      stack: event.error instanceof Error ? event.error.stack : undefined,
    });
  });

  window.addEventListener('unhandledrejection', (event: PromiseRejectionEvent) => {
    const reason = event.reason;
    if (reason instanceof Error) {
      reportClientError({
        message: `Unhandled rejection: ${reason.message}`,
        stack: reason.stack,
      });
    } else {
      reportClientError({
        message: `Unhandled rejection: ${typeof reason === 'string' ? reason : JSON.stringify(reason ?? 'unknown')}`,
      });
    }
  });
}
